import type { Item } from "../types/items";
import type { UserLight } from "../types/users";

// ─── Finish type ─── single source of truth for the ALUMINIUM/EVO values,
// mirrors the backend's Finish type/constants (services/finish.go).
export type Finish = "ALUMINIUM" | "EVO";

/** Column header only: joist top flush with the column top ("aligned") or `joistTopOffset` below it ("offset"). */
export type JoistTopAlignment = "aligned" | "offset";
/** The position a column header gets when none is chosen: mirrors the backend's JoistTopAlignmentDefault. */
export const DEFAULT_JOIST_TOP_ALIGNMENT: JoistTopAlignment = "offset";
export const FINISH_ALUMINIUM: Finish = "ALUMINIUM";
export const FINISH_EVO: Finish = "EVO";

// ─── LateralResistanceConfig ─── single source of truth for what each
// integer value means, mirrors the backend's LateralResistance* constants
// (models/dto/definition.go).
export const LATERAL_RESISTANCE_CONFIG = {
  HeaderColumnNotch: 0,
  HeaderBeamNotch: 1,
  JoistBeamNotch: 2,
  InclinedFasteners: 3,
  LockStop: 4,
} as const;

// ─── UpliftResistanceConfig ─── mirrors the backend's uplift handling
// (services/calculation_verification.go's useUpliftInclinedFastener): 0
// means the inclined fastener is used for uplift resistance.
export const UPLIFT_RESISTANCE_CONFIG = {
  InclinedFasteners: 0,
} as const;

export type BOMItem = {
  item: Item;
  label: string;
  quantity: number;
  category: "connector" | "fastener" | "lockstop";
};

// ─── Connector (one connector pair — 1 or 2 per Configuration) ───
export type Connector = {
  lockItem?: Lock | null;
  finish?: string | null;
};

// ─── Result (solver output for one Configuration) ───
export type Result = {
  fvD?: number | null;
  faxD?: number | null;
  flatD?: number | null;
  fupD?: number | null;
  activeCombination?: string | null;
  etaCombined?: number | null;
  isVerified?: boolean | null;
  k23Ser?: number | null;
};

// ─── Configuration (one candidate fastening combination, draft or chosen) ───
// headerFastenerItem/joistFastenerItem/inclinedFastenerItem/lockstopItem are
// shared across every pair in `connectors` — not per-connector selections.
export type Configuration = {
  id: string;
  connectors?: Connector[] | null;
  headerFastenerItem?: Fastener | null;
  joistFastenerItem?: Fastener | null;
  inclinedFastenerItem?: Fastener | null;
  lockstopItem?: LockStop | null;
  result?: Result | null;
  /** lock_lockstop_mapping.installation_levels for lockstopItem — null when no lockstop is selected/mapped. */
  installationLevels?: number | null;
};

export type ConnectorUpdate = {
  lockItemId?: string | null;
  finish?: string | null;
};

// ─── ConfigurationUpdate (Configuration-level part of an update payload) ───
export type ConfigurationUpdate = {
  connectors: ConnectorUpdate[];
  headerFastenerItemId?: string | null;
  joistFastenerItemId?: string | null;
  inclinedFastenerItemId?: string | null;
  lockstopItemId?: string | null;
  fvD?: number | null;
  faxD?: number | null;
  flatD?: number | null;
  fupD?: number | null;
};

// ─── DefinitionFieldsUpdate (Definition-level part of an update payload) ───
export type DefinitionFieldsUpdate = {
  standardId?: string | null;
  serviceClassId?: string | null;
  loadDurationClassId?: string | null;
  continuousCtrl?: boolean | null;
  /** User-defined coefficients for this calculation only; null = the normative value. */
  kmodOverride?: number | null;
  gammaMConnectionOverride?: number | null;
  gammaMAluminiumOverride?: number | null;
  lateralResistanceConfig?: number | null;
  upliftResistanceConfig?: number | null;
  connectionCount?: number | null;
  /** Wider header-side lock (MIDI + downward vertical load); null = not chosen. */
  headerLockItemId?: string | null;
  headerOrientation?: string | null;
  alphaDeg?: number | null;
  betaDeg?: number | null;
  /** Distance (mm) from the joist top edge to the connector; null = unset, falls back to the lock module's c_min. */
  connectorTopDistance?: number | null;
  joistTopAlignment?: JoistTopAlignment | null;
  joistTopOffset?: number | null;
  headerMaterialGradeId?: string | null;
  headerB?: number | null;
  headerH?: number | null;
  joistMaterialGradeId?: string | null;
  joistB?: number | null;
  joistH?: number | null;
  verticalLoad?: number | null;
  upliftLoad?: number | null;
  axialLoad?: number | null;
  lateralLoad?: number | null;
  includeFv?: boolean | null;
  includeFup?: boolean | null;
  includeFax?: boolean | null;
  includeFlat?: boolean | null;
};

export type OperationMode = "generateAll" | "generateFromData" | "generateManual";

export type LockCalculatorDefinitionUpdateData = {
  definition: DefinitionFieldsUpdate;
  configuration?: ConfigurationUpdate | null;
  /** Set when the user picked a draft from the generated-configurations table; promotes it to 'chosen' and skips regeneration. */
  selectedConfigurationId?: string | null;
  /** Selects what side-effect (if any) the update performs after saving the form data. Omitted/null means save-only. */
  operationMode?: OperationMode | null;
};

/** Derived message about the Definition's current state; Code is a stable key, Fields are update-body field names. */
export type BackendNotice = {
  code: string;
  severity: "info" | "warning" | "error";
  fields: string[];
  params: Record<string, unknown> | null;
};

// ─── API Definition (mirrors backend JSON response) ───
export type LockCalculatorDefinition = {
  calculationId: string;
  /** Derived (read-only): why the current data cannot be calculated or fails, each tied to the fields that cause it. */
  notices?: BackendNotice[];
  standard?: Standard | null;
  serviceClass?: ServiceClass | null;
  loadDurationClass?: LoadDurationClass | null;
  /** Effective coefficients the solver uses: the override when set, else the normative value. */
  kmod?: number | null;
  gammaMConnection?: number | null;
  gammaMAluminium?: number | null;
  /** The normative value alone — shown next to an override and used by "restore". */
  kmodDefault?: number | null;
  gammaMConnectionDefault?: number | null;
  gammaMAluminiumDefault?: number | null;
  /** User-defined values, per calculation; null = the normative value. */
  kmodOverride?: number | null;
  gammaMConnectionOverride?: number | null;
  gammaMAluminiumOverride?: number | null;
  continuousCtrl?: boolean | null;
  lateralResistanceConfig?: number | null;
  upliftResistanceConfig?: number | null;
  connectionCount?: number | null;
  /** Wider header-side lock (MIDI + downward vertical load); null = not chosen. */
  headerLockItemId?: string | null;
  headerOrientation?: string | null;
  alphaDeg?: number | null;
  betaDeg?: number | null;
  connectorTopDistance?: number | null;
  /** Column header only; null = aligned. */
  joistTopAlignment?: JoistTopAlignment | null;
  /** mm the joist top sits below the header top, offset mode only; null = the lock's c_min. */
  joistTopOffset?: number | null;
  /** Derived (read-only): the distance used when connectorTopDistance is empty, for the first connector's lock. */
  connectorTopDistanceDefault?: number | null;
  /** Derived (read-only): the offset used when joistTopOffset is empty (offset mode: the lock's c_min). */
  joistTopOffsetDefault?: number | null;
  /** Derived (read-only): whether the header needs pre-drilling (ADR-025); null = cannot be decided. */
  headerPreDrilled?: boolean | null;
  /** Derived (read-only): whether the joist needs pre-drilling (ADR-025); null = cannot be decided. */
  joistPreDrilled?: boolean | null;
  headerMaterialGrade?: MaterialGrade | null;
  headerB?: number | null;
  headerH?: number | null;
  joistMaterialGrade?: MaterialGrade | null;
  joistB?: number | null;
  joistH?: number | null;
  verticalLoad?: number | null;
  upliftLoad?: number | null;
  axialLoad?: number | null;
  lateralLoad?: number | null;
  includeFv?: boolean | null;
  includeFup?: boolean | null;
  includeFax?: boolean | null;
  includeFlat?: boolean | null;
  configuration?: Configuration | null;
  draftConfigurations?: Configuration[] | null;
  /** Numero totale di draft: puo' superare draftConfigurations.length (il server ne restituisce solo i migliori). */
  draftConfigurationsTotal?: number | null;
  owner?: UserLight | null;
  editor?: UserLight | null;
  createdAt?: string | null;
  modifiedAt?: string | null;
  billOfMaterials?: BOMItem[] | null;
};

// ─── Catalog Search Types ───
export type CatalogItem = {
  id: string;
  code: string;
  name: string;
};

export type Standard = {
  id: string;
  code: string;
  name: string;
  nameIt?: string | null;
  nameDe?: string | null;
  nameFr?: string | null;
  nameEs?: string | null;
};

export type ServiceClass = {
  id: string;
  classNumber: number;
  name: string;
  nameIt?: string | null;
  nameDe?: string | null;
  nameFr?: string | null;
  nameEs?: string | null;
  description?: string | null;
  typicalExamples?: string | null;
  order: number;
};
export type LoadDurationClass = {
  id: string;
  code: string;
  name: string;
  nameIt?: string | null;
  nameDe?: string | null;
  nameFr?: string | null;
  nameEs?: string | null;
  typicalDuration?: string | null;
  typicalLoads?: string | null;
  order: number;
};
export type MaterialGrade = {
  id: string;
  code: string;
  name: string;
  standardReference?: string | null;
  rhoK?: number | null;
  rhoM?: number | null;
  fc0K?: number | null;
  fc90K?: number | null;
  fvK?: number | null;
  faxK?: number | null;
  kw?: number | null;
  aCoef?: number | null;
  bCoef?: number | null;
};

export type Lock = {
  item: Item;
  /** Mini/Midi product line — lock_calculator.lock.family. */
  family: "MIDI" | "MINI";
  width: number;
  thickness: number;
  /** Metal thickness a fastener passes through on one side of this item
   * (screw insertion, ~6.5-8mm) — distinct from thickness (lock_module's
   * paired-assembly width). Used for the withdrawal/shear lef calculation. */
  screwEdgeThickness?: number | null;
  nModules: number;
  evoAvailable: boolean;
  evoItem?: Item | null;
  lockModule?: LockModule | null;
  nInclinedUplift?: number;
  nInclinedLateral?: number;
  headFastenersCount: number;
  joistFastenersCount: number;
};

/** Lateral resistance calculation_case for a lock_module — see
 * lock_calculator.lock_module_coefficients. The vertical-downward kh2
 * factor lives directly on LockModule.kh2PerModule, not here. */
export type LockModuleCalculationCase = "NOTCH" | "INCLINED" | "LOCKSTOP";

export type LockModuleCoefficient = {
  id: string;
  lockModuleId: string;
  calculationCase: LockModuleCalculationCase;
  nModules: number;
  nef?: number | null;
  klat?: number | null;
};

export type LockModule = {
  id: string;
  code: string;
  /** Nominal/assembly height (names the product, e.g. LOCKT240 -> 240). */
  assemblyHeight: number;
  /** Physical height of a single head/joist part (e.g. 226.9 for LOCKT240). */
  sideHeight?: number | null;
  /** Nullable: not yet known for the LOCK-T FLOOR modules. */
  profileThickness?: number | null;
  moduleWidth: number;
  joistFastenersPerModule?: number | null;
  headFastenersPerModule?: number | null;
  kLockFactor: number;
  f1AluRk: number;
  f2AluRkPerModule: number;
  /** ETA-19/0831 Annex B.1 kh2 factor (vertical-downward); scaled by
   * nModules by the backend. Nullable: not yet seeded for every module. */
  kh2PerModule?: number | null;
  contactAreaALock: number;
  cMin?: number | null;
  /** Per calculation_case, per n_modules lateral lookup rows. */
  coefficients: LockModuleCoefficient[];
};

export type LockAccessory = {
  id: string;
  lock: Lock;
  accessory: Item;
};

export type Fastener = {
  item: Item;
  diameter: number;
  length: number;
  threadLength?: number | null;
  myRk?: number | null;
  ftensK?: number | null;
  headType?: string | null;
};

export type LockStop = {
  item: Item;
  visibility: string;
  fyK?: number | null;
  area?: number | null;
  fLockstopRk?: number | null;
  isSingle: boolean;
  order?: number;
};

export type LockStopLight = {
  id: string;
  description: string;
};

export type SearchLockStopsResponse = {
  items: LockStop[];
  total: number;
};

export type KmodValue = {
  serviceClass: ServiceClass;
  loadDurationClass: LoadDurationClass;
  kmod: number;
};

export type GammaMValue = {
  standardId: string;
  continuousCtrl: boolean | null;
  gammaM: number;
  standardCode?: string | null;
  standardName?: string | null;
};

// ─── Admin types ───

export const NormativeTypes = ["EUROCODE", "NATIONAL_CODE", "HARMONIZED_STANDARD", "ETA", "DOP"] as const;
export type NormativeType = (typeof NormativeTypes)[number];

export type AdminStandard = {
  id: string;
  code: string;
  name: string;
  nameIt?: string | null;
  nameDe?: string | null;
  nameFr?: string | null;
  nameEs?: string | null;
  normativeType: NormativeType;
  jurisdictionCode?: string | null;
  visibility?: string;
  order: number;
};

export type AdminServiceClass = {
  id: string;
  classNumber: number;
  name: string;
  nameIt?: string | null;
  nameDe?: string | null;
  nameFr?: string | null;
  nameEs?: string | null;
  description?: string | null;
  descriptionIt?: string | null;
  descriptionDe?: string | null;
  descriptionFr?: string | null;
  descriptionEs?: string | null;
  typicalExamples?: string | null;
  order: number;
};

export type AdminLoadDurationClass = {
  id: string;
  code: string;
  name: string;
  nameIt?: string | null;
  nameDe?: string | null;
  nameFr?: string | null;
  nameEs?: string | null;
  typicalDuration?: string | null;
  typicalLoads?: string | null;
  order: number;
};

export type AdminMaterialGrade = {
  id: string;
  code: string;
  name: string;
  standardReference?: string | null;
  rhoK?: number | null;
  rhoM?: number | null;
  fc0K?: number | null;
  fc90K?: number | null;
  fvK?: number | null;
  faxK?: number | null;
  kw?: number | null;
  aCoef?: number | null;
  bCoef?: number | null;
  visibility?: string;
  order: number;
};

export type AdminFastener = {
  item: Item;
  diameter: number;
  length: number;
  threadLength?: number | null;
  myRk?: number | null;
  ftensK?: number | null;
  headType?: string | null;
  /** Real screw family (lock_calculator.fastener.screw_family); null when none is set. */
  screwFamily?: string | null;
  visibility?: string;
  order: number;
  /** Behaviours the fastener may take (fastener_usage table). */
  usages?: FastenerUsageType[];
};

/** Behaviour a fastener may take — mirrors lock_calculator.fastener_usage.usage. */
export type FastenerUsageType = "standard" | "inclined";

export type AdminFastenerUsage = {
  fastener: Item;
  usage: FastenerUsageType;
};

export type Admin3DModel = {
  modelFile: string;
  modelFormat: string;
  features?: Record<string, unknown> | null;
  modelData?: string | null;
};

export type Admin3DModelEntityType = "lock_3d" | "lockstop_3d" | "fastener_3d";

export type Admin3DModelRecord = Admin3DModel & {
  modelId: string;
  modelType: Admin3DModelEntityType;
  itemId?: string | null;
  dataSize: number;
};

export type Admin3DModelUploadEntry = {
  clientId: string;
  modelType: Admin3DModelEntityType;
  features?: Record<string, unknown> | null;
  file: File;
};

export type Admin3DModelUploadResult = {
  clientId: string;
  modelType: Admin3DModelEntityType;
  modelFile: string;
  success: boolean;
  error?: string;
  model?: Admin3DModelRecord;
};

export type AdminKmodValue = {
  serviceClass: AdminServiceClass;
  loadDurationClass: AdminLoadDurationClass;
  kmod: number;
};

export type AdminGammaMValue = {
  standardId: string;
  continuousCtrl: boolean | null;
  gammaM: number;
  standardCode?: string | null;
  standardName?: string | null;
};

export type AdminLock = {
  item: Item;
  family: "MIDI" | "MINI";
  width: number;
  thickness: number;
  screwEdgeThickness?: number | null;
  nModules: number;
  evoAvailable: boolean;
  visibility?: string;
  evoItem?: Item | null;
  lockModule?: LockModule | null;
  nInclinedUplift?: number;
  nInclinedLateral?: number;
  headFastenersCount: number;
  joistFastenersCount: number;
  order: number;
};

export type AdminLockModule = LockModule;

export type AdminLockModuleCoefficient = LockModuleCoefficient;

export type AdminLockAccessory = LockAccessory;

export type AdminLockStop = LockStop & {
  order: number;
};

export type AdminLockLockstopMapping = {
  lock: Item;
  lockstop: Item;
  quantity: number;
  installationLevels: number;
};

/** Lockstop compatibile con una COPPIA (non ordinata) di connettori affiancati. */
export type AdminLockPairLockstopMapping = {
  lockA: Item;
  lockB: Item;
  lockstop: Item;
  /** Pezzi totali della coppia per la distinta (non la somma dei due connettori). */
  quantity: number;
  /** Livelli distinti che la resistenza F_LOCKSTOP,Rk moltiplica. */
  installationLevels: number;
};

export type LockLight = {
  id: string;
  evoAvailable: boolean;
  description: string;
  /** Mini/Midi product line — lock_calculator.lock.family. */
  family?: "MIDI" | "MINI";
  /** Inclined fasteners per connector, per failure mode; both 0 = the lock takes no inclined fasteners. */
  nInclinedUplift?: number | null;
  nInclinedLateral?: number | null;
};

export type StandardLight = {
  id: string;
  description: string;
  code: string;
};

export type ServiceClassLight = {
  id: string;
  description: string;
  classNumber: number;
};
export type LoadDurationClassLight = {
  id: string;
  description: string;
  code: string;
};

/** Select option; the values below prefill the custom-material editor and show what a custom one inherits. */
export type MaterialGradeLight = {
  id: string;
  description: string;
  /** A user-defined material from the signed-in user's library. */
  isCustom?: boolean;
  structuralFamily?: string;
  categoryName?: string;
  normativeCode?: string;
  rhoK?: number | null;
  rhoM?: number | null;
  fc0K?: number | null;
  fc90K?: number | null;
  fvK?: number | null;
  frK?: number | null;
  faxK?: number | null;
};

/** A material in the user's library, with how many calculations use it as header or joist. */
export type CustomMaterialGrade = MaterialGradeLight & { usageCount: number };

/** baseGradeId is read on create only; on update a null rhoM / faxK clears it. */
export type CustomMaterialInput = {
  name: string;
  baseGradeId?: string;
  rhoK: number;
  rhoM: number | null;
  fc0K: number;
  fc90K: number;
  fvK: number;
  frK: number;
  faxK: number | null;
};

export type FastenerLight = {
  id: string;
  description: string;
};
